import { Directive, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, AbstractControlOptions, FormArray, FormBuilder, FormControl, FormGroup, NgForm, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { Actions, ofType } from '@ngrx/effects';
import { AuthReduxObject, SafariObject } from '@safarilaw-webapp/shared/common-objects-models';
import { ElmahSeverity } from '@safarilaw-webapp/shared/logging';
import { reduxActionFail, ReduxFormErrorInfo, ReduxWrapperService } from '@safarilaw-webapp/shared/redux';
import * as deepDiff from 'deep-diff';

import { cloneDeep } from 'lodash-es';
import mapValues from 'lodash-es/mapValues';
import { of, Subscription } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, mergeMap, tap } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';

import { IfNotReadyToDisplayDirective } from '../../../shared/directives/if-not-ready-to-display/if-not-ready-to-display.directive';
import { SafariUiFormReduxObject } from '../../../state/actions/layout-actions';
import { FormSubmitInfo } from '../../../state/interfaces/layout-interface';
import { FormControlNameDirective } from '../../directives/form-control-name';
import { dialogControlMixin } from '../safari-base-page/dialog-control-mixin';
import { PageUiService, SafariDumbComponent, SafariSmartComponent } from '../safari-base/safari-base.component';

/*
  ISafariFormComponent

  Basic interface that defines various functions and properties on a form component. It is implemented by SafariEditFormComponent
*/
const DISABLED = 'DISABLED!';
export enum FormUpdateType {
  OnBlur,
  OnSubmit
}
// This works in reverse to extractJsonTypeFromFormGroup
export declare type jsonTypeToFormGroup<T> = T extends string
  ? FormControl<string>
  : T extends number
    ? FormControl<number>
    : T extends boolean
      ? FormControl<boolean>
      : T extends bigint
        ? FormControl<bigint>
        : T extends Array<infer Z>
          ? FormArray<jsonTypeToFormGroup<Z>>
          : T extends Record<string, any>
            ? FormGroup<{ [K in keyof T]: jsonTypeToFormGroup<T[K]> }>
            : any;
export declare type extractJsonTypeFromFormGroup<T> =
  // If the results is unknown we want "any" so that our nontyped forms don't complain
  // about assigning and reading from unknown
  unknown extends T
    ? any
    : // Now let's first check for FormControl (the end result of recursion). If it's a FormControl
      // it can only be number, bool, string so just infer the type and return
      T extends FormControl<infer U>
      ? U
      : // This is always going to be the entry point but also can be called in recursion (object within an object)
        // If we are formgroup we are going to infer the type again and call extract recursively which sends us back to the top
        T extends FormGroup<infer U>
        ? extractJsonTypeFromFormGroup<U>
        : // At this point this must be the object that was inferred from the first FormGroup or in a recursive call before this one
          // But this type might also have other formcontrols so let's account for that
          {
            [K in keyof T]: T[K] extends FormGroup | FormControl // (FormControl will be the end of the line in the next call) // If the key is another FormGroup or another FormControl call itself and dig deeper
              ? extractJsonTypeFromFormGroup<T[K]>
              : // But this can also be a FormArray - in that case just recursively call extract on the array's inferred type
                T[K] extends FormArray<infer G>
                ? Array<extractJsonTypeFromFormGroup<G>>
                : // Otherwise if this couldn't be resolved (unknown) return any, otherwise return the type that it is (string, nubmer, etc)
                  unknown extends T[K]
                  ? any
                  : T[K];
          };

/**
 * Statement below returns JSON object like
 * {
 *    name: string;
 *    nameArray: [string];
 *    email: string;
 *    amount: number;
 *    test2: {
 *       newVar: string;
 *       groupArray: [
 *          {
 *              testSubgroup: string;
 *          }
 *       ]
 *    }
 * }
 *
 * Essentially all the Form fluff was stripped from the object below
 *
 * let test: extractJsonTypeFromFormGroup<
 * FormGroup<{
 *   name: FormControl<string>;
 *   nameArray: FormArray<FormControl<string>>;
 *   email: FormControl<string>;
 *   amount: FormControl<number>;
 *   test2: FormGroup<{
 *     newVar: FormControl<string>;
 *     groupArray: FormArray<FormGroup<
 *     {
 *       testSubgroup: FormControl<string>
 *     }>>;
 *   }>
 * }> >
 *
 *
 *
 */

// Statement below just returns any
// let test2: extractJsonTypeFromFormGroup<any>;

/*
  SafariFormComponent

  Child forms will override this class to set up their forms for reading and editing objects. This class
  will handle dispatching form events, submitting form, validation and other form related tasks.
*/
@Directive()
export class SafariFormComponent<T extends SafariObject, T1 extends FormGroup = FormGroup<any>, PageServiceType extends PageUiService = PageUiService>
  extends dialogControlMixin(SafariSmartComponent)<PageServiceType>
  implements OnInit, OnDestroy
{
  /**
   * Common imports for all standalone forms. Additional ones like mask, currency, etc, need to be imported explicitly but these are pretty much the foundation of all our forms
   */
  static standaloneImports = [ReactiveFormsModule, IfNotReadyToDisplayDirective, FormControlNameDirective];
  private _firstControlWithError = null;

  @ViewChild('updateOnSubmit') ngFormElement: NgForm;

  checkForErrorObject = true;

  private _formSubs: Map<string, Subscription> = new Map<string, Subscription>();
  private _readOnly = false;
  private _readOnlySetViaInput = false;
  public get readOnly() {
    return this._readOnly;
  }
  @Input()
  public set readOnly(value) {
    if (this._readOnlySetViaInput && value != this._readOnly) {
      throw new Error('Changing readOnly not allowed once it is set');
    }
    this._readOnlySetViaInput = true;
    this._readOnly = value;

    this.checkReadOnly();
  }
  private _isDirty = false;
  private _originalFormRawValue = null;
  private _lastCheckedFormRawValue = null;
  protected _pathsToExclude: string[] = [];

  private _formReduxObject: SafariUiFormReduxObject;
  private _formUpdateType: FormUpdateType = FormUpdateType.OnBlur;
  private _lastActionId: string;
  _actions: Actions<unknown>;
  protected _fb: FormBuilder;
  protected _appModel: T;

  // eslint-disable-next-line @typescript-eslint/naming-convention --  hidden from children
  private ___reduxWrapper: ReduxWrapperService;
  // TODO: I think AbstractControl<any> should be AbstractControl<K> eventually
  // That should  give us strongtypedness of individual fields as well but if we do that now
  // we'll have a ton of compile errors for components that just use "any" as the default T1
  // because it seems to assume "string" in that case. So we'll first have to probably strong-type
  // all existing form components, by passing an actual object def as T1. Then we can do this
  private _form: T1;

  private _allPropChanges: string[] = [];

  protected checkReadOnly() {
    if (this.form) {
      if (this.readOnly) {
        // _readOnly is a special hidden field that is going to be available to this form's
        // input controls so it can be queried in formControlDirective
        this.form['_readOnly'] = true;
        this.form.disable();
      } else {
        this.form['_readOnly'] = false;
        this.form.enable();
      }
    }
  }
  notifyFormSubmitSuccess(payload: FormSubmitInfo<T, T1>) {
    this.___reduxWrapper.dispatchGenericAction(this._formReduxObject.default.actions.formSubmitSuccess({ payload }));
  }
  notifyFormSubmitFail(payload: FormSubmitInfo<T, T1>, error: ReduxFormErrorInfo[]): void {
    this.___reduxWrapper.dispatchGenericAction(this._formReduxObject.default.actions.formSubmitFail({ payload: { ...payload, errors: error }, error }));
  }

  /*
    lastActionId  - GUID. Assigned by framework for every unique action
  */
  get lastActionId(): string {
    return this._lastActionId;
  }

  /*
    bindTo        - Containers (SafariSingleObjectContainer and SafariMultipleObjectContainer) will usually assign results of their observables
                    to this property. This will then handle initial binding and error checks
  */
  @Input()
  set bindTo(value: T) {
    // Make sure to check for null, otherwise cloneDeep/deepFreeze will fail trying to clone or freeze a NULL object
    if (value == null) {
      this._appModel = null;
    } else {
      this._appModel = cloneDeep(value);
      this._deepFreeze(this._appModel);
    }

    // This will only be the case during subsequent calls to [bindTo], if any. (form can only
    // be nonnull after the intial onModelBound, which is called by ngOnInit)
    // Also - no need to rebind if this attached to an observable that returned 'error' (usually
    // validation error from the API), since that will be the exact same model that was sent

    // NOTE: I think this is an unecessary leftover from when we used to have requiredtobind
    // and called onbound every time a required prop changes. I think the only place that
    // callOnBound should be called automatically is onInit (and we have it there already ).
    // Right now the way this is it can cause bugs such as 6754. But for now we are going to fix 6754 on the form itself,
    // but I think soon we should try removing this soon and retesting.
    // Most of our forms now do full reload after save anyway, so this shouldn't be a problem
    if (this.form && this._appModel?.error == null) {
      this.callOnBound();
    }
    if (this._appModel?.error) {
      this.processApiError();
    }
  }
  get bindTo(): T {
    return this._appModel;
  }

  /*
    model         - Model that this form will be populated with (usually an object that came back from the API)
  */
  get appModel(): T {
    return this._appModel;
  }
  /*
    form          - Represents the form. Children will use this to get to various form properties, etc.
  */

  // TODO: I think AbstractControl<any> should be AbstractControl<K> eventually
  // That should  give us strongtypedness of individual fields as well but if we do that now
  // we'll have a ton of compile errors for components that just use "any" as the default T1
  // because it seems to assume "string" in that case. So we'll first have to probably strong-type
  // all existing form components, by passing an actual object def as T1. Then we can do this

  get form(): T1 {
    return this._form;
  }
  /*
    formBuilder         - Represents the form builder. Children will use this to create the form.
  */
  get formBuilder(): FormBuilder {
    return this._fb;
  }
  constructor() {
    super();
    this.___reduxWrapper = this.inject(ReduxWrapperService);

    this._authReduxObject = this.inject(AuthReduxObject);

    this._fb = this.inject(FormBuilder);

    this._formReduxObject = this.inject(SafariUiFormReduxObject);
    this._actions = this.inject(Actions);
  }

  private _deepFreeze(object) {
    // Retrieve the property names defined on object
    const propNames = Object.getOwnPropertyNames(object);
    for (const name of propNames) {
      const value = object[name];
      if (value) {
        if (typeof value === 'object') {
          object[name] = this._deepFreeze(value);
        } else if (Array.isArray(value)) {
          object[name] = this._deepFreeze(value);
        }
      }
    }
    return Object.freeze(object);
  }
  readyToDisplay() {
    return this.form != null;
  }

  private callOnBound(changedProps: string[] = []) {
    this.onModelBound();
    this._resetFormSnapshot();

    if (this.readOnly) {
      this.checkReadOnly();
    }
  }
  private _resetFormSnapshot() {
    if (this.form) {
      this._originalFormRawValue = cloneDeep(this.form.getRawValue());
      this._lastCheckedFormRawValue = this._originalFormRawValue;
    } else {
      this._originalFormRawValue = this._lastCheckedFormRawValue = null;
    }

    this._isDirty = false;
  }

  isPropertyBound(name: string) {
    return this._allPropChanges.includes(name);
  }
  private _appModelEtagsMatch(appModelCached) {
    return appModelCached['__etag'] == this.appModel['__etag'];
  }
  ngOnInit() {
    super.ngOnInit();
    if (this.componentId == null) {
      throw new Error('Component id not set for the form');
    }
    this.callOnBound();
  }

  /**
   * Blank out all form subs (but not necessarily component subs)
   * This will be called by ngOnDestroy but can also be used to destroy a previously created
   * form and rebind to a new one for the same component, without tearing down the component itself
   */
  destroyForm() {
    this._store.dispatch(this._formReduxObject.default.actions.formClearFormData({ id: this.componentId }));
    super.clearAllCustomSubscriptions(this._formSubs);
    this._form = null;
    this._resetFormSnapshot();
  }
  override ngOnDestroy() {
    this.destroyForm();
    super.ngOnDestroy();
  }
  /*
    createFormSubmitSubscription - Creates FormSubmit subscription. The form will listen to messages that carry its' form ID
                                   and will call the this.submit() function.
                                   NOTE: There is a subtle difference between FormSubmit and RequestFormSubmit messages.
                                   FormSubmit message contains the form data. If invoked by the framework it will contain
                                   the data that the form has on screen and that is its' most common use. However, certain
                                   effects and other actions could also do some additional form data modifications (currently
                                   CompoundInfoRequestSave does this to modify the payload and submit close portal events)
  */
  createFormSubmitSubscription() {
    this.subscribe(
      this._actions.pipe(
        ofType(this._formReduxObject.default.actions.formSubmit),
        mergeMap(action =>
          of(action).pipe(
            filter(o => o.payload.id === this.componentId),
            catchError(err => {
              this._store.dispatch(
                reduxActionFail({
                  originalPayload: action.payload,

                  payload: action.payload,
                  reduxErrorOptions: { mustResolve: true, url: window.location.href, source: 'SafariFormComponent.createFormSubmitSubscription()' },
                  error: err
                })
              );
              return of(err);
            })
          )
        )
      ),
      action => {
        try {
          this.submit(action.payload);
        } catch (o) {
          this._store.dispatch(this._formReduxObject.default.actions.formSubmitFail({ payload: action.payload, error: o }));
          this._store.dispatch(
            reduxActionFail({
              payload: action.payload,
              originalPayload: action.payload,
              originalOptions: action.options,
              context: action.additionalInfo,
              error: o,
              reduxErrorOptions: {
                mustResolve: true,
                url: window.location.href,
                source: 'SafariFormComponent.createFormSubmitSubscription()'
              }
            })
          );
        }
      },
      this._formSubs
    );
  }
  createCancelEditSubscription() {
    super.subscribe(this._reduxWrapper.listenForAction(this._formReduxObject.default.actions.cancelEdit), () => {
      this.ngFormElement.onSubmit(undefined);
      this.form['suppressValidationMessages'] = true;
    });
  }
  /*
    createformRequestSubmitSubscription - Creates FormRequestSubmit subscription. This message will gather form data from the
                                          form and send FormSubmit message using that data. (indirectly, by calling requestSubmit).
                                          This can be used to submit a particular form "remotely"

  */
  createformRequestSubmitSubscription() {
    this.subscribe(
      this._actions.pipe(
        ofType(this._formReduxObject.default.actions.formRequestSubmit),
        mergeMap(o =>
          of(o).pipe(
            filter((payload: { id: string; additionalInfo: any }) => payload.id === this.componentId),
            catchError(err => {
              this._store.dispatch(
                reduxActionFail({
                  payload: o,
                  originalPayload: o,

                  error: err,
                  reduxErrorOptions: {
                    mustResolve: true,
                    url: window.location.href,
                    source: 'SafariFormComponent.createformRequestSubmitSubscription()'
                  }
                })
              );
              return of(o);
            })
          )
        )
      ),
      payload => {
        try {
          this.requestSubmit(payload.additionalInfo);
        } catch (o) {
          this._store.dispatch(
            reduxActionFail({
              payload,
              error: o,

              originalPayload: o.payload,
              reduxErrorOptions: {
                mustResolve: true,
                url: window.location.href,
                source: 'SafariFormComponent.createformRequestSubmitSubscription()'
              }
            })
          );
        }
      },
      this._formSubs
    );
  }
  /*
    createformRequestPatchSubscription - Creates FormRequestPatch subscription. Sometimes we want to force some form values from outside 
                                         of the form. 
  */
  createformRequestPatchSubscription() {
    this.subscribe(
      this._actions.pipe(
        ofType(this._formReduxObject.default.actions.formRequestPatch),
        mergeMap(o =>
          of(o).pipe(
            filter((payload: { id: string; formValues: any }) => payload.id === this.componentId),
            catchError(err => {
              this._store.dispatch(
                reduxActionFail({
                  payload: o,
                  originalPayload: o,

                  error: err,
                  reduxErrorOptions: {
                    mustResolve: true,
                    url: window.location.href,
                    source: 'SafariFormComponent.createformRequestPatchSubscription()'
                  }
                })
              );
              return of(o);
            })
          )
        )
      ),
      payload => {
        try {
          this.form.patchValue(cloneDeep(payload.formValues));
        } catch (o) {
          this._store.dispatch(
            reduxActionFail({
              payload,
              error: o,

              originalPayload: o.payload,
              reduxErrorOptions: {
                mustResolve: true,
                url: window.location.href,
                source: 'SafariFormComponent.createformRequestSubmitSubscription()'
              }
            })
          );
        }
      },
      this._formSubs
    );
  }

  protected dispatchApiValidationError(error, payload) {
    this._store.dispatch(reduxActionFail({ error, originalPayload: payload, payload, reduxErrorOptions: { mustResolve: true, url: window.location.href, source: 'SafariFormComponent.bind()' } }));
  }
  protected processApiError() {
    if (!this.checkForErrorObject) {
      return;
    }

    // Extract error from the appModel THEN create payload object that contains everything but the error object
    // No real effect to error handling but would like to see the original payload in ngrx tools
    // (and everything in the appmodel of error object IS the original payload - refer to ObjectActionTypes.CreateOrUpdateObjectFail
    // in redux library reducer.ts)
    const { error, ...payload } = this._appModel as any;
    // This will cause reduxActionFail to be raised and then caught by error-handler-service. We should
    // get away from this and just raise a dialog here eventually.
    this.dispatchApiValidationError(error, payload);
  }
  onModelBound() {
    // Child components are supposed to inherit this.
    // This is where you will create the form and convert model
    // to form's own model representation
  }
  /*
    touchAllFormFields    - Simply marks all fields on the form as touched. This is called by submit and
                            will signal to the validation directive (formControlNameDirective) that it should display
                            form validation messages if there are any
  */
  touchAllFormFields() {
    this.forAllFields((ctrl: AbstractControl, name: string, parent: FormGroup | FormArray) => {
      ctrl.markAllAsTouched();
      ctrl.updateValueAndValidity();
    }, this.form);
  }
  /**
   *
   * @param fn Traverses the entire form and children and calls a callback function for each
   *  control
   * @param parent
   * @returns
   */
  public forAllFields(fn: (field: AbstractControl, name: string, parent: FormGroup | FormArray) => void, parent: FormGroup | FormArray = null): void {
    if (parent == null) {
      this.forAllFields(fn, this.form);
      return;
    }

    Object.keys(parent.controls).forEach((key: string) => {
      const abstractControl = parent.controls[key];
      if (abstractControl != null) {
        if (abstractControl instanceof FormGroup || abstractControl instanceof FormArray) {
          if (abstractControl instanceof FormGroup) {
            fn(abstractControl, key, parent);
          }
          this.forAllFields(fn, abstractControl);
        } else {
          fn(abstractControl, key, parent);
        }
      }
    });
  }

  /*
    onSubmit    - This will be called after the form has passed validation. Children need to implemnent this to do
                  whatever is needed for their particular case
  */
  onSubmit(submitInfo: FormSubmitInfo<T, extractJsonTypeFromFormGroup<T1>>): void {
    this.notifyFormSubmitSuccess(submitInfo);
  }
  /*
    getFormValidationErrors   - Gathers all validation errors. Note that currently we're logging those in console as well.
                                If you ever run into a case where your form worked fine just a second ago but now you
                                click Save and your form appears to do nothing check the console. It's quite likely that
                                there is a field on your page that is somehow not visible (maybe covered by another field, hidden
                                in some collapsed div or some other bug like that) but is failing validation
  */
  getFormValidationErrors(): ReduxFormErrorInfo[] {
    // First reset firstControlWithError element
    this._firstControlWithError = null;
    return this.parseFormForErrors(null, this.form.controls);
  }

  private _addAndLogError(key: string, control: AbstractControl<any, any>, errors: ReduxFormErrorInfo[]) {
    const controlErrors = control.errors;
    if (controlErrors != null) {
      // If this is the first control that we found that has an error then set fistControlWithError
      // element. This will be used later for scrolling
      if (this._firstControlWithError == null) {
        this._firstControlWithError = control;
      }
      Object.keys(controlErrors).forEach(keyError => {
        errors.push({
          keyControl: key,
          keyError,

          error: controlErrors[keyError]
        });

        const message = 'Key control: ' + key + ', keyError: ' + keyError + ', err value: ' + (controlErrors[keyError] as string);
        // eslint-disable-next-line no-console -- We want to keep this console.log
        console.log(message);
      });
    } else {
      if (!control.valid && control.disabled) {
        // It this control has no errors but its invalid due to being disabled
        // just add the error but don't log yet. We don't care about this if the rest of
        // the form is invalid for valid reasons
        errors.push({
          keyControl: key,
          keyError: DISABLED,
          error: DISABLED
        });
      }
    }
  }
  private _parseFormArrayErrors(arrayKey: string, controlArray: FormArray, errors: ReduxFormErrorInfo[]) {
    if (controlArray?.controls == null) {
      return;
    }

    const controls = controlArray.controls;
    Object.keys(controls).forEach(key => {
      const control = controls[key];
      const fullKey = (arrayKey || '') + '-' + key;
      if (control instanceof FormGroup) {
        this.parseFormForErrors(fullKey, control.controls, errors);
      } else if (control instanceof FormArray) {
        this._parseFormArrayErrors(fullKey, control, errors);
      } else if (control instanceof FormControl) {
        this._addAndLogError(fullKey, control, errors);
      } else {
        // Don't think this is possible but just in case, so we don't end up with an empty log
        const message = 'Unknown control type' + typeof control + ', form ' + this.componentId;
        // eslint-disable-next-line no-console -- we want this one
        console.error(message);
        this._logger.LogInfoMessageToElmah('Unknown control type', message, null, ElmahSeverity.Error);
      }
    });
  }
  parseFormForErrors(formKey: string, controls: { [key: string]: AbstractControl }, errors: ReduxFormErrorInfo[] = []): ReduxFormErrorInfo[] {
    if (controls == null) {
      return;
    }

    Object.keys(controls).forEach(key => {
      const fullKey = (formKey || '') + '-' + key;
      const control = controls[key];
      if (control instanceof FormGroup) {
        this.parseFormForErrors(fullKey, control.controls, errors);
      } else if (control instanceof FormArray) {
        this._parseFormArrayErrors(fullKey, control, errors);
      }

      this._addAndLogError(fullKey, control, errors);
    });
    return errors;
  }
  /*
    getFormSubmitInfo - Gets the latest form state by going to this.form.value (alternatively could have read it from the store
                        as well since the form keeps it up to date in store as well)
                        Used by requestFormSubmit OR by some more advanced/complex effects
                        At the time of this writing SettingsGeneralEditFormComponent is a good example of this alternative use
  */
  getFormSubmitInfo(additionalInfo = null): FormSubmitInfo<T, T1> {
    this._lastActionId = uuidv4();

    return {
      // NOTE: We are using getRawValue() so we can get values of disabled controls as well
      // By default HTML (and NG) will return only controls that are not disabled.
      formModel: this.form.getRawValue(),
      appModel: this._appModel,
      id: this.componentId,
      additionalInfo,
      actionId: this._lastActionId
    } as FormSubmitInfo<T, T1>;
  }
  /*
    requestSubmit - Shortcut for dispatching formsumit message with the form's current data.
                    Containers should avoid calling this directly (as that would require acquiring form component(s)
                    from template resulting in calls like form1.requestSubmit() form2.requestSubmit etc. It would get
                    even worse if the forms are nested deeper). A better approach for a container is to send FormRequestSubmit
                    message with the desired form ID and let the form pick it up whereever it may be
  */
  requestSubmit(additionalInfo = null) {
    // If we use onsubmit updatetype we have to call OnSubmit on ngForm manually. This is because if NG's onSubmit
    // form mode the expectation is that there will be a submit button that triggers submit on the page at which point
    // NG will update form values. However, we do not use submit buttons - in many cases our buttons are outside of the form
    // or even broadcasting "submit" event. Therefore without this call the form would never "see" the new values it has and
    // nothing would be changed or validated.
    if (this._formUpdateType === FormUpdateType.OnSubmit) {
      // https://stackoverflow.com/questions/38054631/angular2-validate-and-submit-form-from-outside
      // P.S. This is the only approach it worked - I tried using ngFormElement.ngSubmit.emit() as well as ngFormElement.nativeElement.submit()
      // but both ended in weird behavior with form refreshing and nothing getting submitted
      // Main excerpt from the article:
      // ********************************
      // Normally if you have <button type='submit'>Submit</button> (inside the <form> tag) it will trigger the standard HTML <form> to submit
      // (nothing to do with Angular) - and that raises a standard submit event on the form tag.
      // IF that <form> tag also has a [formGroup] directive on it (as shown above) then the HTML form submit event is 'caught' by the directive
      // and that's what causes the onSubmit() function above to be called.
      // This in turn raises the ngSubmit event - which you can catch yourself if you need to do further processing - like showing an alert.
      // So it's very important to call onSubmit and not ngSubmit.emitto get the validation handling to work when using material (reactive form) controls.
      // The event parameter can just be null or undefined.
      // ********************************
      this.ngFormElement.onSubmit(undefined);
    }
    delete this.form['suppressValidationMessages'];
    this._store.dispatch(this._formReduxObject.default.actions.formSubmit({ payload: this.getFormSubmitInfo(additionalInfo) }));
  }
  getPresubmitValidationError() {
    return null;
  }
  /*
    onPreSubmitValidate - Called during the submit process to validate the form
  */
  onPreSubmitValidate(): boolean {
    const validationMessage = this.getPresubmitValidationError();
    if (!String.isNullOrEmpty(validationMessage)) {
      this.showValidationError(validationMessage);
      return false;
    } else {
      return true;
    }
  }
  applySoftReqRules(group: string, control: AbstractControl): boolean {
    return false;
  }
  resetValidators() {
    this.forAllFields((ctrl: AbstractControl, name: string, parent: FormGroup | FormArray) => {
      ctrl.clearValidators();
      ctrl.setErrors(null);
      ctrl.updateValueAndValidity();
      this._cdr.detectChanges();
      ctrl.markAsPristine();
      ctrl.markAsUntouched();
    }, this.form);
  }
  triggerValidation() {
    // This will trigger validation errors to show up (via FormControlNameDirective)
    this.touchAllFormFields();
    this._cdr.detectChanges();
  }
  private _getFocusableElement(element) {
    if (element == null) {
      return null;
    }
    if ((element as HTMLElement).nodeName?.toUpperCase() == 'NG-MULTISELECT-DROPDOWN') {
      return element;
    }
    const validElements = ['INPUT', 'TEXTAREA', 'SELECT'];

    if (!validElements.includes(element.nodeName?.toUpperCase())) {
      if (element.children != null && element.children.length > 0) {
        for (const child of element.children) {
          const result = this._getFocusableElement(child);
          if (result) {
            return result;
          }
        }
        return null;
      } else {
        return null;
      }
    } else {
      return element;
    }
  }
  /*
    submit          - Called when the form processes FormSubmit message and it receives the payload from the message
    PARAMETERS
    submitInfo      - information about the form, including latest form values (or custom payload if used in advanced cases)
  */
  submit(submitInfo: FormSubmitInfo<T, T1>) {
    this.triggerValidation();
    if (this.form.disabled || (this.form.valid && this.onPreSubmitValidate())) {
      // If the form is valid OR disabled (doesn't make sense to call validate on disabled forms) call onSubmit (implemented by children)
      // This method will be implemented in child components and will map payload from the form to the model that needs
      // to be sent to the API
      return this.onSubmit(cloneDeep(submitInfo) as any);
    } else {
      // Unblock UI if form validation fails
      this.dispatchToggleBlockUi(false);
      const formDisabledError: ReduxFormErrorInfo[] = [];
      if (this.form.disabled) {
        formDisabledError.push({
          error: 'Form is disabled. Do not validate',
          keyControl: 'FORM',
          keyError: DISABLED
        });
      }

      const errors = [...formDisabledError, ...this.getFormValidationErrors()];
      // Track form validation in appinsights. Note that we want to remove those special "DISABLED!"
      // errors. Those are only important if the only errors that were thrown were disabled and nothing else
      // (which means we're somehow validating a disabled form which is not good. And we have an elmah
      // exception for that)
      this._logger.trackEvent('FormValidation', { errors: errors.filter(o => o.keyError != DISABLED) });
      // We don't care to show disabled error IF there are actual validation errors in the form
      // Some code is written in a way where it will not necessary remove disabled controls at the time
      // it's popping some other validations (like requiredness), but once the user selects something it
      // removes disabled controls. So the end result is everything works fine.
      // We really only need to log this IF THIS IS THE ONLY ERROR on the form, meaning that somehow
      // we tried to submit a form that was disabled, and no other validations kicked in. This would
      // result in user getting "Save failed" without seeing any errors on screen
      if (errors.find(o => o.keyError != DISABLED) == null) {
        for (const error of errors) {
          const message = 'Validation occuring on disabled element in ' + this.componentId + ', element ' + error.keyControl;
          // eslint-disable-next-line no-console -- We want to keep this console.log
          console.error(message);
          this._logger.LogInfoMessageToElmah('Disabled Form Validation', message, null, ElmahSeverity.Error);
        }
      }

      // Some client validation failed
      this._store.dispatch(this._formReduxObject.default.actions.formSubmitFail({ error: errors, payload: { ...submitInfo, errors } }));

      // Validations that are not field specific will not result in "_firstControlWithError" as there is really
      // no control to error out on. So let's make sure this is not null.
      if (this._firstControlWithError) {
        // First get a native element from the first control with error
        const firstElementWithError = this._firstControlWithError['_nativeElement'];

        // Now we have to find something that we can actually focus to. For regular HTML tags, such as
        // input, text etc this will return the same object. But for 3rd party controls INPUT/TEXTAREA/SELECT
        // might be wrapped into numerous divs, spans and what not. This call will drill down into hierearchy
        // until it finds first input/text/etc
        const focusableelement = this._getFocusableElement(firstElementWithError) || firstElementWithError;

        // If we didn't find any focusable elements this would be the same as firstElementWithError. We wouldn't
        // be able to focus but we can still scroll to it
        if (focusableelement) {
          this.expandAllContainingTwisties(focusableelement.parentElement);
          // I've seen scroll/focus sometimes not work so adding a timeout here.
          // Quite possibly that sometimes it tries to focus before the accordion is done expanding
          // so timeout should help. 250ms should be enough for expansion to complete
          setTimeout(() => {
            if (typeof focusableelement.scrollIntoView == 'function') {
              // Let's first focus (this may or may not work on some 3rd party components)
              // Also - make sure to prevent scroll - we are going to use scrollintoview instead which gives
              // us more control of where we want scroll to happen. If we dont stop focus from scrolling
              // scrollIntoView will ignore the options passed into it
              (focusableelement as HTMLElement).focus({ preventScroll: true });
              // We used to have "smooth" behavior but on larger pages (matter edit) it's a bit glitchy,
              // Instant doesn't look as cool, but it doesn't result in jitter either
              focusableelement.scrollIntoView({ behavior: 'instant', block: 'center', inline: 'center' });
            }
          }, 50);
        }
      }
    }
    return of(null);
  }
  protected getCreateFeatureFlagFormType() {
    return {};
  }
  protected getPatchFeatureFlagFormType() {
    return {};
  }

  /*
    createForm          - Called by children to create the form

    PARAMETERS

    formObj             - Form object that was build by the child component

  */
  createForm(formObj: any, formUpdateType = FormUpdateType.OnBlur): boolean {
    // NOTE!!! If you are using FormUpdateType.OnSubmit the form associated with this component must
    // include the following directive:
    // #updateOnSubmit="ngForm"

    if (this._form == null) {
      const options: AbstractControlOptions = formUpdateType === FormUpdateType.OnBlur ? {} : { updateOn: 'submit' };
      this._formUpdateType = formUpdateType;
      this._form = this._fb.group({ ...formObj, ...this.getCreateFeatureFlagFormType() }, options) as any;
      this.form['_safariForm'] = this;
      this.createValueChangeSubscription();
      this.createFormSubmitSubscription();
      this.createformRequestSubmitSubscription();
      this.createformRequestPatchSubscription();
      if (formUpdateType === FormUpdateType.OnSubmit) {
        // Cancel action will be sent automatically when cancel() is called on SafariMultiObjectContainer.
        // It's imperative that we listen for it if the form uses onSubmit type. OnSubmit form must call OnSubmit in order
        // to get new values and without this call during cancel operation we wouldnt' be able to catch dirty values and
        // show dirty page warning
        this.createCancelEditSubscription();
      }
      // Make sure to call setReadyToDisplay as soon as the form is created. Creation of the form by itself does not cause ngOnChanges
      // to fire
      super.setReadyToDisplay(this.readyToDisplay());

      return true;
    }
    return false;
  }
  registerSelfSyncingControl(path: string) {
    if (this.form.get(path) == null) {
      throw new Error('Registering self-synching control failed. Path not found: ' + path);
    }

    this.subscribe(
      this.form.get(path).valueChanges,
      x => {
        this.form.get(path).setValue(x, { onlySelf: true, emitEvent: false });
      },
      this._formSubs
    );
  }
  patchForm(formObj: any = {}) {
    // When patching the form we want to reset, since in case of rebinding while on the same page (polling for example)
    // we want to make the form pristine, untouched, and so forth.
    // But at the same time we don't want this to trigger valueChange events otherwise the subs that were created earlier will
    // respond with null values
    this.form.reset(
      undefined, // simulating the default value (nothing passed int)
      {
        onlySelf: false, // this is the default
        emitEvent: false // this is our override - do not emit
      }
    );
    // "Normalize" undefineds to NULL. This should be a very rare case (most likely result of some missing fields coming back from the API
    // OR from some inmemory model), but could potentially lead to some silly bugs like dirty page warning showing up right away (for this to
    // happen patch would have to happen asynch , right after first onmodelbound. This in itself is also probably fairly rare occurence, but could
    // be a part of the workflow)
    // So just set all undefined values to NULL when the form is patched
    this.form.patchValue(
      mapValues({ ...formObj, ...this.getPatchFeatureFlagFormType() }, (value, key) => {
        if (value === undefined) {
          return null;
        }
        return value;
      })
    );
  }
  /*
    createValueChangeSubscription          -  Any time anything changes on the form we update the store
                                              This ensures that store will have latest and greatest state of
                                              this form and anyone can read it any time
  */
  static areObjectsDifferent(obj1, obj2, pathsToExlude = []) {
    const diffs = deepDiff.diff(obj1, obj2);
    if (diffs != null) {
      for (const diff of diffs) {
        if (diff.path == null) {
          // This will be the case when two arrays are compared and the first one has different
          // number of items than the second one
          return true;
        }

        // When we have NULL values returned from the API they will be bound as NULL
        // to the form, as expected. But then if you type, and then delete all the way back the form
        // will set it to '' and therefore the original NULL won't be equal to '', and the
        // Save button will remain lit.
        // So we have four options:
        // 1. When assigning to form.patchValue set all initial NULL values to ''. This might
        //    not work well though, and would require a ton of updates and maintenance
        // 2. Ignore diffs between NULL and ''
        // 3. Add hostlistener on key down to our formControlName directive and do something like
        // @HostListener('keyup', ['$event']) onKeyDowns(event: KeyboardEvent) {
        //  if (this.ngControl.value?.trim() === '') {
        //      this.ngControl.reset(null);
        //  }
        //}
        // This might work but it changes the behavior of all input fields, so we'd have to be careful
        // 4. Accept this as "by design" and ignore it
        // For now I m taking the 2nd approach, but if it causes any issues we might want
        // to take this out try number 3 or just accept it as by-design
        if ((diff['lhs'] == null && diff['rhs'] === '') || (diff['rhs'] == null && diff['lhs'] === '')) {
          continue;
        }
        // Also - deepdiff is type sensitive and won't compare string to numbers,
        // so we'll do an extra check ourselves (sometimes we bind enums for example which are ints
        // but they seem to be converted to strings in the dropdown)
        if ((typeof diff['lhs'] == 'number' && typeof diff['rhs'] == 'string') || (typeof diff['rhs'] == 'number' && typeof diff['lhs'] == 'string')) {
          if (diff['rhs'] == diff['lhs']) {
            continue;
          }
        }
        // We are going to "normalize" all array expressions to .0.
        // So for example subjects.12.showAddress will become subjects.0.showAddress
        // To set path arrays to exclude just use 0 for index in the child constuctor
        const fullPath = diff.path.join('.').replace(new RegExp('\\.(\\d+)\\.'), '.0.');

        if (!pathsToExlude.includes(fullPath)) {
          // If paths to exclude represents an array then anything inside of it shouldn't be
          // checked so let's check that condition as well
          let isSubpath = false;
          if (fullPath.includes('.')) {
            pathsToExlude.forEach(pathToExclude => {
              // If what we got a diff on (for example participantRows.0.isReviewer)
              // begins with what we have in pathsToExlude (participantRows for example)
              // then this signfies an array or subobject, and since we are excluding the
              // entire subobject/array we should skip this and not mark as dirty
              if (fullPath.startsWith(pathToExclude + '.')) {
                isSubpath = true;
              }
            });
          }

          if (isSubpath) {
            break;
          }
          return true;
        }
      }
    }
    return false;
  }
  createValueChangeSubscription() {
    this.subscribe(
      this.form.valueChanges.pipe(
        tap(() => {
          // This registers form changes as user activity for the purpose of auto logout
          this._store.dispatch(this._authReduxObject.default.actions.updateLastActivityTimestamp());
        }),
        // We're going to use getRawValue() instead of valueChanges. The reason is that valueChanges
        // will only return enabled controls. getRawValue will return everything, including disabled
        map(() => this.form.getRawValue()),
        tap(formRawValue => {
          // I *think* that we should not do this until originalformvalue has something in it
          // The reason being that this event will trigger on initial form creation and at that point nothing
          // was bound yet. However this will set isDirty to true briefly because it's comparing originalFormValueAsJSON string
          // (whihc will be NULL since nothing was bound yet) WITH getRawValue which will represent default form values before binding.
          // Needless to say these two will be different

          // Don't bother checking  for isDirty if we're in readOnly form

          if (this._originalFormRawValue != null && !this._readOnly) {
            // If we asked for some controls to be excluded we'll use cloneDeep in combination with deepdiff and ignore those
            // Maybe if we find out that this is performing well in all cases we can just use this and avoid code branching
            // based on "excluded controls", but for now we're only doing this if this property was set
            this._isDirty = SafariFormComponent.areObjectsDifferent(this._originalFormRawValue, formRawValue, this._pathsToExclude);
          }
        }),
        // The next step is to filter out the events that are the same as the last one. This is because
        // we don't want to dispatch the same form state twice, or even worse end up in an infinite loop and freeze the browser
        // NOTE: This statement has nothing to do with dirty change checks. It might be tempting to think
        // "why not just pass isDirty here? Why would we ever want to trigger state update if isDirty is false?"
        // That's because there could be some controls in the form that we explicitly want to exclude from dirty check
        // Maybe they are hidden and used for some internal operation, maybe they are disabled , and so forth.
        // But form state should ALWAYS be updated in the store, regardless of whether it's dirty or not for the purpose of saving.
        filter(formRawValue => JSON.stringify(this._lastCheckedFormRawValue) !== JSON.stringify(formRawValue))
      ),
      formRawValue => {
        const lastActionId = uuidv4();

        // Let's set lastChecked here, so that the next time we compare agains this value
        this._lastCheckedFormRawValue = formRawValue;

        this._store.dispatch(
          this._formReduxObject.default.actions.formUpdate({
            payload: {
              // NOTE: We want to use getRawValue call instead of values that would be usually
              // passed in via subscribe method. The reason is that the latter would return only
              // enabled fields and we'd really want to have the full form state here rather than
              // just enabled fields
              formModel: this.form.getRawValue(),
              originalForm: this._originalFormRawValue,
              id: this.componentId,
              actionId: lastActionId,
              appModel: this._appModel
            } as FormSubmitInfo<T, T1>
          })
        );
      },
      this._formSubs
    );
  }
  protected showValidationError(message: string, title: string = 'Validation Error') {
    super.showDialog1ButtonOnce$('Validation Error', message).subscribe();
  }
  /*
    getAdditionalParamsForUpdate          -   Before dispatching the framework will call this function. If overriden in children
                                              it can return an object with some information. This object will be stored in
                                              'additionalInfo' property of the message and can be later queried by some custom
                                              update effect that understands its' meaning.
  */
  getAdditionalParamsForUpdate(): any {
    return null;
  }
  /*
    hasUnsavedChanges          -   Simple dirty check
  */
  hasUnsavedChanges() {
    if (this._readOnly) {
      return false;
    }
    // First get the value of super.hasUnsavedChanges.
    // Most forms don't have any additional #checkChanges component so this will be false in most cases
    // but for those that do we need this call
    const superHasUnsavedChanges = super.hasUnsavedChanges();
    if (this.form == null) {
      return superHasUnsavedChanges;
    }

    // #3197 - in case of emergency uncomment the code below to go back to this.form.dirty
    // return this.form.dirty || superHasUnsavedChanges;
    return this._isDirty || superHasUnsavedChanges;
  }

  // Let's make sure we use valueChanges/enable/disable from this class, rather than
  // calling the form directly. This will take in account readOnly and also won't fire
  // valueChanges on things like changing validators or disabled/enabled state
  valueChanges<P extends AbstractControl | string>(path: P) {
    if (this.form == null) {
      return;
    }
    if (path == null) {
      return;
    }
    if (path instanceof AbstractControl) {
      return path.valueChanges.pipe(distinctUntilChanged());
    }

    return this.form.get(path).valueChanges.pipe(distinctUntilChanged());
  }
  // If you have some weird situation where one specific control on the form can be toggled by viewer
  // (hope not) then override this
  enable<P extends AbstractControl | string>(path: P) {
    if (this.readOnly || !this.form || !path) {
      return;
    }
    if (path instanceof AbstractControl) {
      path.enable();
      return;
    }

    this.form.get(path).enable();
  }
  disable<P extends AbstractControl | string>(path: P) {
    if (!this.form || !path) {
      return;
    }
    if (path instanceof AbstractControl) {
      path.disable();
      return;
    }
    this.form.get(path).disable();
  }
  removeRequired<P extends AbstractControl | string>(path: P) {
    if (path == null) {
      return;
    }
    if (!this.form) {
      return;
    }
    if (path instanceof AbstractControl) {
      path.removeValidators(Validators.required);
      return;
    }

    this.removeValidators(path, Validators.required);
  }
  addRequired<P extends AbstractControl | string>(path: P) {
    if (path == null) {
      return;
    }
    if (!this.form) {
      return;
    }
    if (path instanceof AbstractControl) {
      path.addValidators(Validators.required);
      return;
    }

    this.addValidators(path, Validators.required);
  }
  clearValidators<P extends AbstractControl | string>(path: P) {
    if (path == null) {
      return;
    }
    if (!this.form) {
      return;
    }
    if (path instanceof AbstractControl) {
      path.clearValidators();
      path.updateValueAndValidity();
      return;
    }

    this.form.get(path).clearValidators();
    this.form.get(path).updateValueAndValidity();
  }
  addValidators<P extends AbstractControl | string>(path: P, validators: ValidatorFn | ValidatorFn[]) {
    if (path == null) {
      return;
    }
    if (!this.form) {
      return;
    }
    if (path instanceof AbstractControl) {
      path.addValidators(validators);
      path.updateValueAndValidity();
      return;
    }

    this.form.get(path).addValidators(validators);
    this.form.get(path).updateValueAndValidity();
  }
  removeValidators<P extends AbstractControl | string>(path: P, validators: ValidatorFn | ValidatorFn[]) {
    if (path == null) {
      return;
    }
    if (!this.form) {
      return;
    }
    if (path instanceof AbstractControl) {
      path.removeValidators(validators);
      path.updateValueAndValidity();
      return;
    }

    this.form.get(path).removeValidators(validators);
    this.form.get(path).updateValueAndValidity();
  }
  setValue<P extends AbstractControl | string>(path: P, value) {
    if (path == null) {
      return;
    }
    if (!this.form) {
      return;
    }
    if (path instanceof AbstractControl) {
      path.setValue(value);
      path.updateValueAndValidity();
      return;
    }

    this.form.get(path).setValue(value);
    this.form.get(path).updateValueAndValidity();
  }
  getValue<P extends AbstractControl | string>(path: P) {
    if (this.form == null) {
      return null;
    }
    if (path == null) {
      return;
    }
    if (path instanceof AbstractControl) {
      return path.value;
    }

    if (!this.form) {
      return;
    }
    return this.form.get(path).value;
  }
}

/**
 * SafariFormSection is a special type of dumb control whose main purpose is splitting a large form 
 * into smaller pieces. They are not real "independent" components. It is still the main form that contains
 * all the form definitions, subscriptions and what not
 * 
 * To use: 
 * 
 * @Component({
  selector: 'sl-my-form-section',
  templateUrl: './my-form-section.component.html',
  styleUrls: ['./my-form-section.component.scss']
  })
 * export class MyFormSection extends SafariFormSection 
 * {
 *    // DO NOT declare form property - it's already in the base
 *    // ALSO Do not use the form property in TS file. It should only be used in the HTML template 
 *    // to bind to form so "formControlName" directive can work. 
 *    // form subs should be done in the main form control if this is to be a true dumb component
 *    @Input()
 *    Prop1
 *    @Input()
 *    Prop2
 *    ...
 * }
 * 
 * THen in parent form's (this is the form that derives from SafariFormComponent) HTML
 * <form>
 * <sl-my-form-section-1 [form]="form" [prop1]=... [prop2]=...>  
 * <sl-my-form-section-2 [form]="form" [prop1]=... [prop2]=...> 
 * <sl-my-form-section-3 [form]="form" [prop1]=... [prop2]=...> 
 * <sl-my-form-section-4 [form]="form" [prop1]=... [prop2]=...> 
 * <sl-my-form-section-5 [form]="form" [prop1]=... [prop2]=...> 
 * </form>
 */
@Directive()
export class SafariFormSection<PageServiceType extends PageUiService = PageUiService> extends SafariDumbComponent<PageServiceType> {
  private _form: FormGroup;
  public get form(): FormGroup {
    return this._form;
  }
  @Input()
  public set form(value: FormGroup) {
    this._form = value;
  }
}
